import { useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  ExternalLink,
  FileText,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { EmptyState } from '@/components/empty-state';
import { FileDrop } from '@/components/file-drop';
import { FileViewer } from '@/components/file-viewer';
import { IconButton } from '@/components/icon-button';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { Pager } from '@/components/pager';
import {
  useDeleteDocument,
  useDocuments,
  useUpdateDocument,
  useUploadDocument,
} from '@/lib/documents';
import { useFormError } from '@/lib/form-error';
import type { DocumentData } from '@/types/api';

const CATEGORY_LABEL: Record<string, string> = {
  contract: 'Contract',
  msa: 'MSA',
  sow: 'SOW',
  runbook: 'Runbook',
  diagram: 'Diagram',
  warranty: 'Warranty',
  license: 'License',
  receipt: 'Receipt',
  other: 'Other',
};

export function ClientDocuments({ clientId }: { clientId: number }) {
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useDocuments(clientId, page);
  const data = paginated?.data;
  const meta = paginated?.meta;
  const remove = useDeleteDocument(clientId);
  const confirm = useConfirm();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Look the viewed/edited docs up from the live list so changes (title rename,
  // expiration update, replacement) flow through without re-opening the modal.
  const viewing = viewingId != null ? (data ?? []).find((d) => d.id === viewingId) ?? null : null;
  const editing = editingId != null ? (data ?? []).find((d) => d.id === editingId) ?? null : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" /> Upload document
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Upload contracts, MSAs, runbooks, receipts, warranties — anything you want to keep with this client."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.map((d) => (
              <DocCard
                key={d.id}
                doc={d}
                onView={() => setViewingId(d.id)}
                onEdit={() => setEditingId(d.id)}
                onDelete={async () => {
                  const ok = await confirm({
                    title: `Delete ${d.title}?`,
                    body: 'It comes off this client and out of the portal, with its file.',
                    confirmLabel: 'Delete document',
                    tone: 'danger',
                  });
                  if (ok) {
                    await remove.mutateAsync(d.id);
                    toast.success('Document deleted');
                  }
                }}
              />
            ))}
          </div>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </>
      )}

      <UploadDialog
        clientId={clientId}
        open={open}
        onClose={() => setOpen(false)}
      />

      {editing ? (
        <EditDocumentDialog
          doc={editing}
          clientId={clientId}
          onClose={() => setEditingId(null)}
        />
      ) : null}

      <Dialog
        open={viewing != null}
        onClose={() => setViewingId(null)}
        title={viewing?.title ?? 'Document'}
        size="lg"
      >
        {viewing && viewing.file_url ? (
          <div className="h-[70vh] w-full overflow-auto rounded border border-[var(--color-border)] bg-[var(--color-surface-muted)]">
            <FileViewer
              url={viewing.file_url}
              mimeType={viewing.mime_type}
              fileName={viewing.file_name}
            />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function DocCard({
  doc,
  onView,
  onEdit,
  onDelete,
}: {
  doc: DocumentData;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const expiresSoon =
    doc.days_until_expiry != null && doc.days_until_expiry >= 0 && doc.days_until_expiry <= 30;
  const expired = doc.days_until_expiry != null && doc.days_until_expiry < 0;

  return (
    <Card>
      <CardHeader className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <FileText className="size-4 text-[var(--color-foreground-muted)]" />
            <button
              type="button"
              onClick={onView}
              className="truncate text-left text-sm font-medium hover:underline"
            >
              {doc.title}
            </button>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{CATEGORY_LABEL[doc.category] ?? doc.category}</Badge>
            {doc.expires_at ? (
              <Badge tone={expired ? 'danger' : expiresSoon ? 'warning' : 'neutral'}>
                {expired ? (
                  <AlertTriangle className="mr-1 size-3" />
                ) : (
                  <CalendarClock className="mr-1 size-3" />
                )}
                {expired
                  ? `Expired ${Math.abs(doc.days_until_expiry!)} d ago`
                  : `Expires ${doc.expires_at}`}
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <IconButton onClick={onEdit} aria-label="Edit document">
            <Pencil className="size-4" />
          </IconButton>
          {doc.file_url ? (
            <IconButton onClick={() => window.open(doc.file_url!, '_blank')} aria-label="Open file">
              <ExternalLink className="size-4" />
            </IconButton>
          ) : null}
          <IconButton onClick={onDelete} aria-label="Delete document">
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      </CardHeader>
      {doc.description ? (
        <CardBody className="whitespace-pre-wrap text-xs text-[var(--color-foreground-muted)]">
          {doc.description}
        </CardBody>
      ) : null}
    </Card>
  );
}

function UploadDialog({
  clientId,
  open,
  onClose,
}: {
  clientId: number;
  open: boolean;
  onClose: () => void;
}) {
  const upload = useUploadDocument(clientId);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('contract');
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const { error, wrap, clear } = useFormError();
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!file) {
      setFileError('Choose a file to upload.');
      return;
    }
    setFileError(null);
    clear();
    const ok = await wrap(() =>
      upload.mutateAsync({
        category,
        title: title || file.name,
        description: description || undefined,
        expires_at: expiresAt || undefined,
        file,
      }),
    );
    if (ok) {
      setFile(null);
      setTitle('');
      setCategory('contract');
      setDescription('');
      setExpiresAt('');
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Upload document" size="lg">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <FileDrop file={file} onChange={setFile} />
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-xs">
            Title
            <Input
              placeholder={file?.name ?? 'Document title'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            Category
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </label>
          <label className="col-span-2 space-y-1 text-xs">
            Description (optional)
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            Expires (optional)
            <Input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
        </div>
        {fileError ? <p className="text-sm text-[var(--color-danger)]">{fileError}</p> : null}
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="submit" disabled={upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}


function EditDocumentDialog({
  doc,
  clientId,
  onClose,
}: {
  doc: DocumentData;
  clientId: number;
  onClose: () => void;
}) {
  const update = useUpdateDocument(clientId);
  const { error, wrap } = useFormError();
  const [title, setTitle] = useState(doc.title);
  const [category, setCategory] = useState(doc.category);
  const [description, setDescription] = useState(doc.description ?? "");
  const [expiresAt, setExpiresAt] = useState(doc.expires_at ?? "");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      update.mutateAsync({
        id: doc.id,
        patch: {
          title,
          category,
          description: description || null,
          expires_at: expiresAt || null,
        },
      }),
    );
    if (ok) onClose();
  }

  return (
    <Dialog open onClose={onClose} title={`Edit ${doc.title}`} size="lg">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 space-y-1 text-xs">
            Title
            <Input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="space-y-1 text-xs">
            Category
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </Select>
          </label>
          <label className="space-y-1 text-xs">
            Expires
            <Input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
          <label className="col-span-2 space-y-1 text-xs">
            Description (optional)
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </div>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
